import ReactGA from 'react-ga4';

/**
 * GA4 Event options compatible with react-ga4's UaEventOptions
 * Note: react-ga4 uses Universal Analytics event structure for compatibility
 */
export interface GA4Event {
  action: string;
  category: string;
  label?: string;
  value?: number;
  [key: string]: unknown;
}

/**
 * Initialize Google Analytics 4
 * @param googleAnalyticsTrackingCode - GA4 measurement ID (e.g., 'G-XXXXXXXXXX')
 */
export const initGA = (googleAnalyticsTrackingCode: string): void => {
  if (!googleAnalyticsTrackingCode) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Google Analytics tracking code is required');
    }
    return;
  }

  try {
    ReactGA.initialize(googleAnalyticsTrackingCode);
  } catch (error) {
    // Only log errors in development to avoid console noise in production
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to initialize Google Analytics:', error);
    }
  }
};

/**
 * Log a page view to Google Analytics
 * @param url - The page URL (will be sanitized to remove trailing slashes)
 * @param pageTitle - Optional page title (defaults to document.title)
 */
export const logPageView = (url: string, pageTitle?: string): void => {
  if (!url) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Page view URL is required');
    }
    return;
  }

  try {
    // Sanitize URL: remove trailing slash
    const sanitizedURL = url.endsWith('/') && url.length > 1 ? url.slice(0, -1) : url;

    // Use provided title or fall back to document.title
    const title = pageTitle ?? (typeof document !== 'undefined' ? document.title : '');

    ReactGA.send({ hitType: 'pageview', page: sanitizedURL, title });
  } catch (error) {
    // Only log errors in development to avoid console noise in production
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to log page view:', error);
    }
  }
};

/**
 * Log a custom event to Google Analytics
 * @param event - GA4 event object with action, category, and optional label, value, etc.
 *                Can also be a string (event name) for simple events
 */
export const logEvent = (event: GA4Event | string): void => {
  if (!event) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Event is required');
    }
    return;
  }

  try {
    // If event is a string, pass it directly
    if (typeof event === 'string') {
      ReactGA.event(event);
      return;
    }

    // Validate object event
    if (!event.action || !event.category) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Event object requires both action and category');
      }
      return;
    }

    ReactGA.event(event);
  } catch (error) {
    // Only log errors in development to avoid console noise in production
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to log event:', error);
    }
  }
};
